import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useChatInterview, LANGUAGES } from '../hooks/useChatInterview';
import SiteHeader from '../components/SiteHeader';
import useAuthModal from '../hooks/useAuthModal';
import AIDisclosureBadge from '../components/AIDisclosureBadge';
import FirstTimeAIDisclosureModal from '../components/FirstTimeAIDisclosureModal';
import JobDetailsContent from '../components/JobDetailsContent';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import FinalReportCard from '../components/interview/FinalReportCard';
import ManualVacancyPanel from '../components/interview/ManualVacancyPanel';
import InterviewHeader from '../components/interview/InterviewHeader';
import ProgressRail from '../components/interview/ProgressRail';
import QuestionAnswerGroup from '../components/interview/QuestionAnswerGroup';
import WelcomeScreen from '../components/interview/WelcomeScreen';
import InterviewInput from '../components/interview/InterviewInput';
import FinalReport from '../components/interview/FinalReport';
import CompetencyAnalysis from '../components/interview/CompetencyAnalysis';
import CollapsibleSection from '../components/interview/CollapsibleSection';
import AiProcessingPopup from '../components/interview/AiProcessingPopup';
import { useAuthStore } from '../stores/useAuthStore';
import { api } from '../api/client';
import { trackEvent } from '../utils/amplitude';

export default function ChatScreen() {
  const { jobId } = useParams();
  const authModal = useAuthModal();
  const user = useAuthStore((state) => state.user);

  useEffect(() => { trackEvent('interview_start'); }, []);
  const updateUser = useAuthStore((state) => state.updateUser);
  const [allowBars, setAllowBars] = useState(Boolean(user?.capabilities?.allow_bars_scoring));

  const { state, actions } = useChatInterview();

  const {
    messages,
    input,
    isLoading,
    isBootstrapping,
    isFinished,
    interviewId,
    questionCount,
    questionStep,
    currentQuestion,
    error,
    jobTitle,
    companyName,
    activeTab,
    vacancyIdForDetails,
    vacancyDescription,
    isStarted,
    selectedLanguage,
    showLangMenu,
    showLimitModal,
    limitErrorMessage,
    aiProcessingStatus,
    messagesEndRef,
    textareaRef,
    isManualVacancyContext,
    navigationVacancyId,
  } = state;

  const {
    setInput,
    setActiveTab,
    setShowLangMenu,
    setShowLimitModal,
    handleUnauthorized,
    handleStartInterview,
    handleFinishNow,
    handleRestartInterview,
    handleStartNewInterview,
    handleSendMessage,
    handleLanguageChange,
  } = actions;

  const [expandedGroups, setExpandedGroups] = useState({});
  const prevCurrentGroupRef = useRef(null);

  useEffect(() => {
    setAllowBars(Boolean(user?.capabilities?.allow_bars_scoring));
  }, [user?.capabilities?.allow_bars_scoring]);

  useEffect(() => {
    let active = true;

    const refreshCapabilities = async () => {
      try {
        const profile = await api.get('/api/auth/profile');
        if (!active || !profile?.capabilities) return;

        const nextCapabilities = profile.capabilities;
        setAllowBars(Boolean(nextCapabilities.allow_bars_scoring));
        updateUser({ capabilities: nextCapabilities });
      } catch (err) {
        console.warn('[Chat] Failed to refresh capabilities:', err);
      }
    };

    refreshCapabilities();
    return () => {
      active = false;
    };
  }, [updateUser]);

  const interviewGroups = useMemo(() => {
    const groups = [];
    let currentGroup = null;
    let questionNumber = 0;

    messages.forEach((message) => {
      if (message.variant === 'question') {
        questionNumber += 1;
        currentGroup = {
          id: message.id,
          questionNumber,
          question: message,
          answer: null,
          feedback: null,
        };
        groups.push(currentGroup);
        return;
      }

      if (!currentGroup) {
        return;
      }

      if (message.role === 'user' && !currentGroup.answer) {
        currentGroup.answer = message;
        return;
      }

      if (message.variant === 'summary' && !currentGroup.feedback) {
        currentGroup.feedback = message;
      }
    });

    return groups;
  }, [messages]);

  const completedCount = useMemo(
    () => interviewGroups.filter((group) => Boolean(group.answer)).length,
    [interviewGroups],
  );
  const answeredQuestionsCount = useMemo(
    () => interviewGroups.filter((group) => Boolean(group.answer?.content?.trim())).length,
    [interviewGroups],
  );

  const totalQuestions = questionCount || 20;
  const progressValue = Math.round((completedCount / totalQuestions) * 100);

  const currentGroup = useMemo(() => {
    if (interviewGroups.length === 0) {
      return null;
    }
    return interviewGroups.find((group) => !group.answer) || interviewGroups[interviewGroups.length - 1];
  }, [interviewGroups]);

  const currentIndicator = Math.min(
    Math.max(currentGroup?.questionNumber || questionStep || 1, 1),
    totalQuestions,
  );

  const currentQuestionText = currentGroup?.question?.content || currentQuestion?.text || '';

  const extractBarsCoverage = useCallback((bars) => {
    if (!bars || typeof bars !== 'object') return {};

    const coverage = {};
    const register = (scaleId, payload = {}) => {
      const sid = String(scaleId || '');
      const score = Number(payload?.score);
      if (!sid || !Number.isFinite(score) || score <= 0) return;
      if (!coverage[sid] || score > Number(coverage[sid].score || 0)) {
        coverage[sid] = {
          score,
          confidence: payload?.confidence ?? null,
        };
      }
    };

    register(bars.target_scale, bars);
    if (bars.cross_signals && typeof bars.cross_signals === 'object') {
      Object.entries(bars.cross_signals).forEach(([scaleId, payload]) => {
        register(scaleId, payload);
      });
    }

    return coverage;
  }, []);

  const finalMessages = useMemo(
    () => messages.filter((message) => message.variant === 'final'),
    [messages],
  );
  const chatBottomSpacerClass = isFinished
    ? 'h-36 sm:h-40'
    : 'h-72 sm:h-80';

  const draftKey = useMemo(() => {
    if (!interviewId || !currentIndicator || isFinished) return null;
    return `interviewDraft:${interviewId}:q${currentIndicator}`;
  }, [interviewId, currentIndicator, isFinished]);

  useEffect(() => {
    if (!draftKey) {
      setInput('');
      return;
    }

    try {
      const savedDraft = localStorage.getItem(draftKey);
      setInput(savedDraft || '');
    } catch (err) {
      console.warn('[Chat] Failed to load draft:', err);
      setInput('');
    }
  }, [draftKey, setInput]);

  const autoResizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 120);
    textarea.style.height = `${newHeight}px`;
  }, [textareaRef]);

  useEffect(() => {
    autoResizeTextarea();
  }, [input, autoResizeTextarea]);

  const handleInputChange = useCallback((event) => {
    const nextValue = event.target.value;
    setInput(nextValue);

    if (draftKey) {
      try {
        if (nextValue.trim()) {
          localStorage.setItem(draftKey, nextValue);
        } else {
          localStorage.removeItem(draftKey);
        }
      } catch (err) {
        console.warn('[Chat] Failed to persist draft:', err);
      }
    }

    window.setTimeout(autoResizeTextarea, 0);
  }, [autoResizeTextarea, draftKey, setInput]);

  useEffect(() => {
    if (!currentIndicator) return;

    setExpandedGroups((prev) => {
      const next = { ...prev };
      const previousCurrent = prevCurrentGroupRef.current;

      if (previousCurrent && previousCurrent !== currentIndicator) {
        next[previousCurrent] = false;
      }

      if (typeof next[currentIndicator] === 'undefined' || previousCurrent !== currentIndicator) {
        next[currentIndicator] = true;
      }

      return next;
    });

    if (prevCurrentGroupRef.current && prevCurrentGroupRef.current !== currentIndicator) {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(`question-set-${currentIndicator}`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    prevCurrentGroupRef.current = currentIndicator;
  }, [currentIndicator]);

  const scrollToQuestionSet = useCallback((targetQuestionNumber) => {
    const target = document.getElementById(`question-set-${targetQuestionNumber}`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const handleToggleGroup = useCallback((groupNumber) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupNumber]: !prev[groupNumber],
    }));
  }, []);

  const handleSendWithUi = useCallback(async () => {
    if (!input.trim() || isLoading || isFinished || isBootstrapping) {
      return;
    }

    const keyAtSend = draftKey;
    const sent = await handleSendMessage();
    if (!sent) {
      return;
    }

    if (keyAtSend) {
      try {
        localStorage.removeItem(keyAtSend);
      } catch (err) {
        console.warn('[Chat] Failed to clear draft:', err);
      }
    }
  }, [draftKey, handleSendMessage, input, isBootstrapping, isFinished, isLoading]);

  useEffect(() => {
    if (!isFinished) return;

    setExpandedGroups(() => Object.fromEntries(interviewGroups.map((group) => [group.questionNumber, false])));
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [interviewGroups, isFinished]);

  if (!isStarted && !isBootstrapping && !isFinished) {
    return (
      <div className="min-h-screen bg-[#FFFBF7] text-[#1E293B]">
        <SiteHeader authModal={authModal} />

        <main className="mx-auto flex max-w-4xl flex-col items-center space-y-16 px-6 py-12">
          <div className="mb-8 flex justify-center">
            <div className="flex items-center rounded-2xl border border-gray-100 bg-white p-1 shadow-sm">
              <button
                onClick={() => setActiveTab('chat')}
                className={`rounded-xl px-6 py-2 text-sm font-semibold transition-all ${activeTab === 'chat'
                  ? 'bg-orange-50 text-orange-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                Interview
              </button>
              <button
                onClick={() => setActiveTab('details')}
                className={`rounded-xl px-6 py-2 text-sm font-semibold transition-all ${activeTab === 'details'
                  ? 'bg-orange-50 text-orange-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                {isManualVacancyContext ? 'Vacancy' : 'Job Details'}
              </button>
            </div>
          </div>

          <div style={{ display: activeTab === 'chat' ? 'block' : 'none' }}>
            <WelcomeScreen
              jobTitle={jobTitle}
              selectedLanguage={selectedLanguage}
              showLangMenu={showLangMenu}
              onStart={handleStartInterview}
              onLanguageToggle={() => setShowLangMenu(!showLangMenu)}
              onLanguageSelect={handleLanguageChange}
              languages={LANGUAGES}
              questionCount={totalQuestions}
            />
          </div>

          {activeTab === 'details' &&
            (isManualVacancyContext ? (
              <ManualVacancyPanel
                vacancyId={navigationVacancyId || jobId}
                fallbackTitle={jobTitle}
                onUnauthorized={handleUnauthorized}
              />
            ) : (
              <JobDetailsContent
                jobId={vacancyIdForDetails || jobId}
                vacancyId={vacancyIdForDetails || navigationVacancyId || null}
                jobTitle={jobTitle}
                vacancyDescription={vacancyDescription}
              />
            ))}
        </main>

        <SubscriptionLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          errorMessage={limitErrorMessage}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#FAF8F5] text-[#1E293B]">
      <SiteHeader authModal={authModal} />
      <FirstTimeAIDisclosureModal />

      <main className={`mx-auto w-full max-w-[740px] flex-1 px-3 py-3 sm:px-6 sm:py-5 ${activeTab === 'chat' ? 'pb-[320px] sm:pb-[360px]' : 'pb-6'}`}>
        <div className="flex justify-end mb-2">
          <AIDisclosureBadge />
        </div>
        <InterviewHeader
          companyName={companyName}
          jobTitle={jobTitle}
          completedCount={completedCount}
          totalQuestions={totalQuestions}
          progressPercent={progressValue}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isManualVacancy={isManualVacancyContext}
        />

        {activeTab === 'chat' && (
          <section className="relative mt-3 space-y-3">
            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {isFinished && (
              <div className="space-y-3">
                <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-center text-[14px] font-bold text-green-700 shadow-sm">
                  ✅ Interview completed! Check your final report below.
                </div>

                {/* Final Report & Competency Analysis above questions */}
                {finalMessages.map((message) => (
                  <div key={`report-container-${message.id}`} className="space-y-3 animate-in fade-in slide-in-from-top-4 duration-700">
                    {message.structuredReport ? (
                      <FinalReport report={message.structuredReport} />
                    ) : (
                      <CollapsibleSection icon="📋" title="Final Report" subtitle="AI assessment">
                        <div className="px-5 py-4 text-[14px] text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {message.content}
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Aggregate BARS Coverage */}
                    {(() => {
                      const coverage = {};
                      interviewGroups.forEach(g => {
                        const barsCoverage = extractBarsCoverage(g.feedback?.bars);
                        Object.entries(barsCoverage).forEach(([sid, payload]) => {
                          if (!coverage[sid] || Number(payload.score || 0) > Number(coverage[sid].score || 0)) {
                            coverage[sid] = payload;
                          }
                        });
                      });
                      
                      if (Object.keys(coverage).length > 0 || !allowBars) {
                        return (
                          <CollapsibleSection icon="📊" title="Competency Analysis" subtitle="Performance across scales">
                            <CompetencyAnalysis coverageMatrix={coverage} locked={!allowBars} />
                          </CollapsibleSection>
                        );
                      }
                      return null;
                    })()}
                  </div>
                ))}
              </div>
            )}

            {isBootstrapping && interviewGroups.length === 0 && !error && (
              <div className="flex flex-col items-center gap-2 py-10 text-sm text-gray-500 animate-pulse">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-orange-500" />
                Initializing expert session...
              </div>
            )}

            <div className="space-y-2.5">
              {interviewGroups.map((group) => (
                <QuestionAnswerGroup
                  key={group.id}
                  questionNumber={group.questionNumber}
                  totalQuestions={totalQuestions}
                  question={group.question}
                  answer={group.answer}
                  feedback={group.feedback}
                  isCurrent={!isFinished && group.questionNumber === currentIndicator && !group.answer}
                  isExpanded={expandedGroups[group.questionNumber] ?? group.questionNumber === currentIndicator}
                  onToggle={() => handleToggleGroup(group.questionNumber)}
                  allowBars={allowBars}
                />
              ))}

              {isLoading && (
                <div className="flex justify-center py-5">
                  <div className="flex items-center gap-1.5 rounded-full border border-gray-100 bg-white px-4 py-2 shadow-sm">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-orange-400" style={{ animationDelay: '0ms' }} />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-orange-400" style={{ animationDelay: '150ms' }} />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-orange-400" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}

              <div className={chatBottomSpacerClass} aria-hidden="true" />
              <div ref={messagesEndRef} />
            </div>

            <ProgressRail
              currentQuestion={currentIndicator}
              totalQuestions={totalQuestions}
              onQuestionClick={scrollToQuestionSet}
            />
          </section>
        )}

        {activeTab === 'details' &&
          (isManualVacancyContext ? (
            <ManualVacancyPanel
              vacancyId={navigationVacancyId || jobId}
              fallbackTitle={jobTitle}
              onUnauthorized={handleUnauthorized}
            />
          ) : (
            <JobDetailsContent
              jobId={vacancyIdForDetails || jobId}
              vacancyId={vacancyIdForDetails || navigationVacancyId || null}
              jobTitle={jobTitle}
              vacancyDescription={vacancyDescription}
            />
          ))}
      </main>

      {activeTab === 'chat' && (
        <InterviewInput
          input={input}
          onInputChange={handleInputChange}
          onSend={handleSendWithUi}
          onStartNew={handleStartNewInterview}
          onRestart={handleRestartInterview}
          onFinish={handleFinishNow}
          isLoading={isLoading}
          isFinished={isFinished}
          isBootstrapping={isBootstrapping}
          canFinish={answeredQuestionsCount > 0}
          textareaRef={textareaRef}
          currentQuestionNumber={currentIndicator}
          totalQuestions={totalQuestions}
          currentQuestionText={currentQuestionText}
          showDraftSaved={Boolean(input.trim())}
        />
      )}

      <SubscriptionLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        errorMessage={limitErrorMessage}
      />

      <AiProcessingPopup isOpen={Boolean(aiProcessingStatus)} status={aiProcessingStatus} />
    </div>
  );
}
